import React, { useState } from "react";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Box, Tooltip as MuiTooltip, Stack } from "@mui/material";

import { useNavigate } from "react-router-dom";
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import EmptyState from "../../../components/atoms/EmptyState";
import StandartButton from "../../../components/atoms/StandartButton";
import GenericTable from "../../../components/organisms/GenericTable";
import { GenericTableColumn } from "../../../components/organisms/GenericTable/GenericTable.types";
import { ButtonVariant } from "../../../Constant/button";
import { formatNumber } from "../LeadGeneralReportsPage.helpers";
import * as Styled from "../LeadGeneralReportsPage.styled";
import {
    ConnectionTableRow,
    DailyTimePoint,
} from "../LeadGeneralReportsPage.types";

const MAX_BAR_SIZE = 48;
const MAX_BAR_SIZE_COMPACT = 28;

interface Props {
    daily: DailyTimePoint[];
    rows: ConnectionTableRow[];
}

interface ConnIconConfig {
    bg: string;
    color: string;
    src?: string;
    Icon?: React.ComponentType<{ sx?: any }>;
}

const SOURCE_ICON_BG = "#F1F5F9";
const SOURCE_ICON_COLOR = "#64748B";
const SOURCE_ICON_BASE = "/assets/images/typeImages/lead/sources";

const connectionIcon = (sourceType: string): ConnIconConfig => {
    const normalizedSourceType = (sourceType || "").toLowerCase();
    const base = { bg: SOURCE_ICON_BG, color: SOURCE_ICON_COLOR };

    if (normalizedSourceType.includes("facebook")) {
        return { ...base, src: `${SOURCE_ICON_BASE}/fb.png` };
    }
    if (normalizedSourceType.includes("tiktok")) {
        return { ...base, src: `${SOURCE_ICON_BASE}/tt.png` };
    }
    if (normalizedSourceType.includes("linkedin")) {
        return { ...base, src: `${SOURCE_ICON_BASE}/linkedin.png` };
    }
    if (
        normalizedSourceType.includes("source api") ||
        normalizedSourceType.includes("api") ||
        normalizedSourceType.includes("webhook") ||
        normalizedSourceType.includes("webform")
    ) {
        return { ...base, src: `${SOURCE_ICON_BASE}/sapi.png` };
    }
    return { ...base, Icon: HelpOutlineIcon };
};

const SourceIcon: React.FC<{ icon: ConnIconConfig; size?: number }> = ({
    icon,
    size = 16,
}) => {
    if (icon.src) {
        return (
            <img
                src={icon.src}
                alt=""
                style={{
                    width: size,
                    height: size,
                    objectFit: "contain",
                    display: "block",
                }}
            />
        );
    }
    const Icon = icon.Icon ?? HelpOutlineIcon;
    return <Icon sx={{ fontSize: size }} />;
};

const sourceBadge = (name: string) => (
    <Box
        sx={{
            display: "inline-block",
            padding: "3px 10px",
            background: "#F1F5F9",
            color: "#475569",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            whiteSpace: "nowrap",
        }}
    >
        {name}
    </Box>
);

const formatDateLabel = (rawDate: string) => {
    try {
        const parsedDate = new Date(rawDate);
        return parsedDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
        });
    } catch {
        return rawDate;
    }
};

const BarTooltip: React.FC<any> = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <Box
            sx={{
                background: "#ffffff",
                border: "1px solid #E2E8F0",
                borderRadius: 2,
                padding: "10px 12px",
                boxShadow: "0 6px 18px rgba(15,23,42,0.08)",
                fontSize: 12,
            }}
        >
            <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                {formatDateLabel(label)}
            </Box>
            {payload.map((entry: any) => (
                <Stack
                    key={entry.dataKey}
                    direction="row"
                    gap={1}
                    alignItems="center"
                >
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: 1,
                            background: entry.color,
                        }}
                    />
                    <Box sx={{ textTransform: "capitalize" }}>
                        {entry.dataKey}:
                    </Box>
                    <Box sx={{ fontWeight: 600 }}>
                        {formatNumber(entry.value)}
                    </Box>
                </Stack>
            ))}
        </Box>
    );
};

const DailyBreakdown: React.FC<{ row: ConnectionTableRow }> = ({ row }) => (
    <Box
        sx={{
            padding: "16px",
            background: "#F8FAFC",
            borderRadius: 2,
        }}
    >
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
        >
            <Box
                sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#475569",
                }}
            >
                Daily breakdown
            </Box>
            <Box sx={{ fontSize: 12, color: "#64748B" }}>
                {row.sourceType} → {row.destinationType}
            </Box>
        </Stack>
        <Box sx={{ width: "100%", height: 160 }}>
            <ResponsiveContainer>
                <BarChart
                    data={row.dailyBreakdown}
                    margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                    maxBarSize={MAX_BAR_SIZE_COMPACT}
                >
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#E2E8F0"
                        vertical={false}
                    />
                    <XAxis
                        dataKey="date"
                        tickFormatter={formatDateLabel}
                        stroke="#94A3B8"
                        fontSize={10}
                        tickLine={false}
                        axisLine={false}
                        minTickGap={18}
                    />
                    <YAxis
                        stroke="#94A3B8"
                        fontSize={10}
                        tickLine={false}
                        axisLine={false}
                        width={28}
                    />
                    <Tooltip
                        content={<BarTooltip />}
                        cursor={{ fill: "rgba(148, 163, 184, 0.08)" }}
                    />
                    <Bar dataKey="sent" stackId="a" fill="#10B981" />
                    <Bar dataKey="failed" stackId="a" fill="#FF0000" />
                    <Bar
                        dataKey="duplicated"
                        stackId="a"
                        fill="#bdc3c7"
                        radius={[4, 4, 0, 0]}
                    />
                </BarChart>
            </ResponsiveContainer>
        </Box>
    </Box>
);

const CONNECTIONS_DEFAULT_PAGE_SIZE = 10;
const CONNECTIONS_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const ConnectionView: React.FC<Props> = ({ daily, rows }) => {
    const navigate = useNavigate();
    const [pageSize, setPageSize] = useState(CONNECTIONS_DEFAULT_PAGE_SIZE);

    const handleViewDetail = (id: number) => {
        navigate(`/lead-connection-reports/${id}`);
    };

    const columns: GenericTableColumn<ConnectionTableRow>[] = [
        {
            key: "name",
            header: "Connection Name",
            label: "Connection Name",
            width: 2,
            minWidth: 180,
            render: (row) => {
                const icon = connectionIcon(row.sourceType);
                return (
                    <Stack direction="row" gap={1.25} alignItems="center">
                        <Styled.IconChip $bg={icon.bg} $color={icon.color}>
                            <SourceIcon icon={icon} size={16} />
                        </Styled.IconChip>
                        <Box sx={{ fontWeight: 600 }}>{row.connectionName}</Box>
                    </Stack>
                );
            },
        },
        {
            key: "source",
            header: "Source",
            label: "Source",
            width: 1,
            minWidth: 120,
            render: (row) => sourceBadge(row.sourceType || "Unknown"),
        },
        {
            key: "total",
            header: "Total Leads",
            label: "Total Leads",
            align: "right",
            width: 1,
            render: (row) => (
                <Box sx={{ fontWeight: 600 }}>{formatNumber(row.total)}</Box>
            ),
        },
        {
            key: "sent",
            header: "Sent",
            label: "Sent",
            align: "right",
            width: 1,
            render: (row) => (
                <Box sx={{ color: "#10B981", fontWeight: 600 }}>
                    {formatNumber(row.sent)}
                </Box>
            ),
        },
        {
            key: "failed",
            header: "Failed",
            label: "Failed",
            align: "right",
            width: 1,
            render: (row) => (
                <Box sx={{ color: "#FF0000", fontWeight: 600 }}>
                    {formatNumber(row.failed)}
                </Box>
            ),
        },
        {
            key: "duplicated",
            header: "Duplicated",
            label: "Duplicated",
            align: "right",
            width: 1,
            render: (row) => (
                <Box sx={{ color: "#bdc3c7", fontWeight: 600 }}>
                    {formatNumber(row.duplicated)}
                </Box>
            ),
        },
        {
            key: "actions",
            header: "",
            label: "Actions",
            align: "right",
            fixedWidth: 80,
            hideable: false,
            render: (row) => (
                <MuiTooltip title="View detail">
                    <span>
                        <StandartButton
                            iconButton
                            variant={ButtonVariant.BLACK_OUTLINED}
                            onClick={(event: React.MouseEvent) => {
                                event.stopPropagation();
                                handleViewDetail(row.id);
                            }}
                        >
                            <VisibilityOutlinedIcon fontSize="small" />
                        </StandartButton>
                    </span>
                </MuiTooltip>
            ),
        },
    ];

    return (
        <Stack gap={3}>
            <Styled.SectionCard elevation={0}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    mb={2}
                    flexWrap="wrap"
                    gap={1.5}
                >
                    <Box>
                        <Styled.SectionTitle>
                            Throughput Over Time
                        </Styled.SectionTitle>
                        <Styled.SectionSubtitle>
                            Sent, failed and duplicated breakdown
                        </Styled.SectionSubtitle>
                    </Box>
                    <Stack direction="row" gap={1.5} alignItems="center">
                        {[
                            { label: "Sent", color: "#16A34A" },
                            { label: "Failed", color: "#DC2626" },
                            { label: "Duplicated", color: "#6B7280" },
                        ].map((legend) => (
                            <Stack
                                key={legend.label}
                                direction="row"
                                gap={0.5}
                                alignItems="center"
                            >
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        background: legend.color,
                                    }}
                                />
                                <Box sx={{ fontSize: 12, color: "#475569" }}>
                                    {legend.label}
                                </Box>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>

                <Box
                    sx={{
                        width: "100%",
                        height: { xs: 240, sm: 280, md: 320 },
                    }}
                >
                    <ResponsiveContainer>
                        <BarChart
                            data={daily}
                            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                            maxBarSize={MAX_BAR_SIZE}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#F1F5F9"
                                vertical={false}
                            />
                            <XAxis
                                dataKey="date"
                                tickFormatter={formatDateLabel}
                                stroke="#94A3B8"
                                fontSize={11}
                                tickLine={false}
                                axisLine={{ stroke: "#E2E8F0" }}
                                minTickGap={24}
                            />
                            <YAxis
                                stroke="#94A3B8"
                                fontSize={11}
                                tickLine={false}
                                axisLine={false}
                                width={32}
                            />
                            <Tooltip
                                content={<BarTooltip />}
                                cursor={{ fill: "rgba(148, 163, 184, 0.08)" }}
                            />
                            <Bar dataKey="sent" stackId="a" fill="#10B981" />
                            <Bar dataKey="failed" stackId="a" fill="#FF0000" />
                            <Bar
                                dataKey="duplicated"
                                stackId="a"
                                fill="#bdc3c7"
                                radius={[4, 4, 0, 0]}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </Styled.SectionCard>

            <Box>
                <Box mb={1.5}>
                    <Styled.SectionTitle>
                        Connections Detail
                    </Styled.SectionTitle>
                    <Styled.SectionSubtitle>
                        Performance per connection — expand a row to see the
                        daily breakdown
                    </Styled.SectionSubtitle>
                </Box>

                <GenericTable
                    columns={columns}
                    rows={rows}
                    getRowKey={(row) => row.id}
                    emptyState={
                        <EmptyState
                            icon={<HubOutlinedIcon />}
                            title="No connections found"
                            description="No connections match the selected date range or filters."
                        />
                    }
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={CONNECTIONS_PAGE_SIZE_OPTIONS}
                    columnToggle
                    tableName="lead_connections"
                    renderExpanded={(row) => <DailyBreakdown row={row} />}
                />
            </Box>
        </Stack>
    );
};

export default ConnectionView;
